import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { LevelTheme, ROAD_HALF_WIDTH, SeededRandom } from '@lucky-vr/common';
import type { RailPath } from '../gameplay/RailPath.js';
import {
  concreteMaterial,
  darkMetalMaterial,
  emissiveMaterial,
  paintedMetalMaterial,
  wetAsphaltMaterial,
} from './MaterialLibrary.js';
import {
  grassTexture,
  dirtTexture,
  brickTexture,
  checkerTexture,
  gradientSkyTexture,
  sandTexture,
  rockTexture,
  concreteTexture,
  murkTexture,
  waterTexture,
  darkAsphaltTexture,
  facadeTexture,
  SKY_DAY,
  SKY_NIGHT,
  SKY_DESERT,
  SKY_SWAMP,
} from './RetroTextures.js';

interface ThemeSpec {
  sky: () => THREE.Texture | THREE.Color;
  fogColor: number;
  fogDensity: number;
  sun: { color: number; intensity: number; shadows: boolean };
  ambient: { color: number; intensity: number };
  hemi: { sky: number; ground: number; intensity: number } | null;
  roadWetness: number;
  roadTint: number;
  dashColor: number;
  curbColors: [number, number];
}

const THEME_SPECS: Record<LevelTheme, ThemeSpec> = {
  [LevelTheme.Countryside]: {
    sky: () => gradientSkyTexture(SKY_DAY),
    fogColor: 0xd8a8c8, fogDensity: 0.0028,
    sun: { color: 0xffd0e0, intensity: 1.1, shadows: true },
    ambient: { color: 0xf0e0ff, intensity: 0.72 },
    hemi: { sky: 0xb13d92, ground: 0x426e60, intensity: 0.54 },
    roadWetness: 0.62,
    roadTint: 0x8a8f99,
    dashColor: 0xffe066,
    curbColors: [0xff2d95, 0x2ee6d6],
  },
  [LevelTheme.City]: {
    sky: () => gradientSkyTexture(SKY_NIGHT),
    fogColor: 0x1a0f2e, fogDensity: 0.0038,
    sun: { color: 0xaa99ee, intensity: 0.85, shadows: true },
    ambient: { color: 0xccbbff, intensity: 0.8 },
    hemi: { sky: 0x6040a2, ground: 0x292337, intensity: 0.74 },
    roadWetness: 0.88,
    roadTint: 0x747c8c,
    dashColor: 0x2ee6d6,
    curbColors: [0xff2d95, 0x3a3a48],
  },
  [LevelTheme.Tunnel]: {
    sky: () => new THREE.Color(0x050508),
    fogColor: 0x11111a, fogDensity: 0.009,
    sun: { color: 0xffffff, intensity: 0.0, shadows: false },
    ambient: { color: 0xffe8f2, intensity: 1.08 },
    hemi: { sky: 0x68546f, ground: 0x252830, intensity: 0.42 },
    roadWetness: 0.78,
    roadTint: 0x777b82,
    dashColor: 0xffe066,
    curbColors: [0xff2d95, 0xdddddd],
  },
  [LevelTheme.Desert]: {
    sky: () => gradientSkyTexture(SKY_DESERT),
    fogColor: 0xf0b090, fogDensity: 0.002,
    sun: { color: 0xffd8b0, intensity: 1.5, shadows: true },
    ambient: { color: 0xffd8c8, intensity: 0.66 },
    hemi: { sky: 0xe05a6b, ground: 0xc9a06a, intensity: 0.46 },
    roadWetness: 0.08,
    roadTint: 0x9a8980,
    dashColor: 0xffe066,
    curbColors: [0xd45a9a, 0xf0e0b0],
  },
  [LevelTheme.Swamp]: {
    sky: () => gradientSkyTexture(SKY_SWAMP),
    fogColor: 0xaab99a, fogDensity: 0.0065,
    sun: { color: 0xd8e0c0, intensity: 0.6, shadows: true },
    ambient: { color: 0xdde8cc, intensity: 0.72 },
    hemi: { sky: 0xb8c39a, ground: 0x465038, intensity: 0.54 },
    roadWetness: 0.82,
    roadTint: 0x70766e,
    dashColor: 0xc0b040,
    curbColors: [0x4a3826, 0x6e5a40],
  },
};

export class SceneManager {
  readonly scene: THREE.Scene;
  readonly vehicleGroup: THREE.Group;
  readonly enemyGroup: THREE.Group;
  readonly environmentGroup: THREE.Group;
  readonly effectsGroup: THREE.Group;

  private lightsGroup: THREE.Group;
  private sun: THREE.DirectionalLight | null = null;
  private garageRain: THREE.LineSegments | null = null;
  private weatherRain: THREE.LineSegments | null = null;
  private actionLights: THREE.PointLight[] = [];
  private atmosphereClock = 0;
  private railPath: RailPath;
  private theme: LevelTheme;
  private random = new SeededRandom('scene');

  constructor(railPath: RailPath, theme: LevelTheme = LevelTheme.Countryside) {
    this.railPath = railPath;
    this.theme = theme;
    this.random = new SeededRandom(`scene:${theme}`);
    this.scene = new THREE.Scene();

    this.vehicleGroup = new THREE.Group();
    this.vehicleGroup.name = 'vehicle';
    this.scene.add(this.vehicleGroup);

    this.enemyGroup = new THREE.Group();
    this.enemyGroup.name = 'enemies';
    this.scene.add(this.enemyGroup);

    this.environmentGroup = new THREE.Group();
    this.environmentGroup.name = 'environment';
    this.scene.add(this.environmentGroup);

    this.effectsGroup = new THREE.Group();
    this.effectsGroup.name = 'effects';
    this.scene.add(this.effectsGroup);

    this.lightsGroup = new THREE.Group();
    this.lightsGroup.name = 'lights';
    this.scene.add(this.lightsGroup);

    this.buildEnvironment(railPath, theme);
  }

  /** Tear down the current environment and build the given theme's. */
  buildEnvironment(railPath: RailPath, theme: LevelTheme): void {
    this.railPath = railPath;
    this.theme = theme;
    disposeChildren(this.environmentGroup);
    disposeChildren(this.lightsGroup);
    this.garageRain = null;
    this.weatherRain = null;
    this.actionLights = [];
    this.atmosphereClock = 0;

    const spec = THEME_SPECS[theme];
    if (this.scene.background instanceof THREE.Texture) this.scene.background.dispose();
    const sky = spec.sky();
    this.scene.background = sky as THREE.Texture | THREE.Color;
    this.scene.environment = sky instanceof THREE.Texture ? sky : null;
    this.scene.environmentIntensity = theme === LevelTheme.City ? 0.64 : 0.46;
    this.scene.fog = new THREE.FogExp2(spec.fogColor, spec.fogDensity);

    this.setupLighting(spec);
    this.createRoad(spec);
    this.createCurbs(spec.curbColors);
    this.createGarage();
    this.createArch(0.998, 'finish');

    switch (theme) {
      case LevelTheme.Countryside: this.buildCountryside(); break;
      case LevelTheme.City: this.buildCity(); break;
      case LevelTheme.Tunnel: this.buildTunnel(); break;
      case LevelTheme.Desert: this.buildDesert(); break;
      case LevelTheme.Swamp: this.buildSwamp(); break;
    }
    this.createWeather(theme);
  }

  /** Keep the shadow camera centered on the action. */
  updateSunFocus(pos: THREE.Vector3): void {
    if (!this.sun) return;
    this.sun.position.set(pos.x + 30, pos.y + 80, pos.z + 40);
    this.sun.target.position.copy(pos);
    const offsets = [
      new THREE.Vector3(-7, 3.2, -8),
      new THREE.Vector3(7, 2.8, 5),
      new THREE.Vector3(0, 5.5, -2),
    ];
    this.actionLights.forEach((light, index) => light.position.copy(pos).add(offsets[index]));
  }

  updateAtmosphere(dt: number, focus: THREE.Vector3): void {
    if (dt > 0) this.atmosphereClock += dt;
    if (this.garageRain && dt > 0) {
      this.garageRain.position.y -= dt * 7.5;
      if (this.garageRain.position.y < -5) this.garageRain.position.y += 10;
    }
    if (this.weatherRain) {
      this.weatherRain.position.set(focus.x, -((this.atmosphereClock * 9) % 12), focus.z);
    }
  }

  private setupLighting(spec: ThemeSpec): void {
    this.lightsGroup.add(new THREE.AmbientLight(spec.ambient.color, spec.ambient.intensity));

    this.sun = new THREE.DirectionalLight(spec.sun.color, spec.sun.intensity);
    this.sun.position.set(30, 80, 40);
    this.sun.castShadow = spec.sun.shadows;
    if (spec.sun.shadows) {
      this.sun.shadow.mapSize.set(2048, 2048);
      this.sun.shadow.camera.near = 1;
      this.sun.shadow.camera.far = 300;
      this.sun.shadow.camera.left = -70;
      this.sun.shadow.camera.right = 70;
      this.sun.shadow.camera.top = 70;
      this.sun.shadow.camera.bottom = -70;
    }
    this.lightsGroup.add(this.sun);
    this.lightsGroup.add(this.sun.target);

    if (spec.hemi) {
      this.lightsGroup.add(new THREE.HemisphereLight(spec.hemi.sky, spec.hemi.ground, spec.hemi.intensity));
    }

    const practicals: Array<[number, number]> = this.theme === LevelTheme.City
      ? [[0x20eaf0, 115], [0xff168c, 100], [0xffd6a0, 78]]
      : this.theme === LevelTheme.Tunnel
        ? [[0xffc36a, 105], [0xff168c, 82], [0xffe6c0, 68]]
        : this.theme === LevelTheme.Swamp
          ? [[0x86cfa1, 72], [0xff8b4c, 60], [0xd3e5bc, 54]]
          : this.theme === LevelTheme.Countryside
            ? [[0x9adfff, 45], [0xff79bd, 38], [0xffd6a0, 62]]
            : [];
    this.actionLights = practicals.map(([color, intensity]) => {
      const light = new THREE.PointLight(color, intensity * 1.2, 32, 1.65);
      this.lightsGroup.add(light);
      return light;
    });
  }

  private createWeather(theme: LevelTheme): void {
    if (![LevelTheme.Countryside, LevelTheme.City, LevelTheme.Swamp].includes(theme)) return;
    const positions: number[] = [];
    const count = theme === LevelTheme.City ? 210 : 130;
    for (let i = 0; i < count; i++) {
      const x = (this.random.next() - 0.5) * 36;
      const y = this.random.next() * 22;
      const z = (this.random.next() - 0.5) * 58;
      positions.push(x, y, z, x - 0.045, y - 0.5, z + 0.12);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    this.weatherRain = new THREE.LineSegments(
      geometry,
      new THREE.LineBasicMaterial({
        color: theme === LevelTheme.Swamp ? 0xb9d5bd : 0xc9faff,
        transparent: true,
        opacity: theme === LevelTheme.City ? 0.18 : 0.09,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    this.weatherRain.frustumCulled = false;
    this.weatherRain.renderOrder = 2;
    this.environmentGroup.add(this.weatherRain);
  }

  private poseAt(t: number, lateral: number): { pos: THREE.Vector3; yaw: number } {
    return {
      pos: this.railPath.getPosition(t, lateral),
      yaw: this.railPath.getYaw(t),
    };
  }

  // ---------------------------------------------------------------
  // Shared pieces
  // ---------------------------------------------------------------

  private createRoad(spec: ThemeSpec): void {
    const roadMaterial = wetAsphaltMaterial(spec.roadWetness, [3, 1]);
    roadMaterial.color.setHex(spec.roadTint);
    this.environmentGroup.add(
      this.buildRibbon(-ROAD_HALF_WIDTH, ROAD_HALF_WIDTH, 0, {
        material: roadMaterial,
        tile: 7,
        receiveShadow: true,
      })
    );

    // Edge lines
    const lineOut = ROAD_HALF_WIDTH - 0.25;
    const lineIn = ROAD_HALF_WIDTH - 0.4;
    const paint = new THREE.MeshStandardMaterial({ color: 0xdde8eb, roughness: 0.48, metalness: 0.02 });
    this.environmentGroup.add(this.buildRibbon(-lineOut, -lineIn, 0.012, { material: paint }));
    this.environmentGroup.add(this.buildRibbon(lineIn, lineOut, 0.012, { material: paint.clone() }));

    // Dashed center line
    const dashGeos: THREE.BufferGeometry[] = [];
    const dashStep = 9 / this.railPath.length;
    for (let t = 0; t < 1; t += dashStep) {
      const { pos, yaw } = this.poseAt(t, 0);
      const geo = new THREE.PlaneGeometry(0.14, 3.2);
      geo.rotateX(-Math.PI / 2);
      geo.rotateY(yaw);
      geo.translate(pos.x, 0.012, pos.z);
      dashGeos.push(geo);
    }
    this.environmentGroup.add(new THREE.Mesh(
      mergeGeometries(dashGeos),
      new THREE.MeshStandardMaterial({ color: spec.dashColor, roughness: 0.42, metalness: 0.03 })
    ));
  }

  private createCurbs([colorA, colorB]: [number, number]): void {
    const a: THREE.BufferGeometry[] = [];
    const b: THREE.BufferGeometry[] = [];
    const step = 2.4 / this.railPath.length;
    let index = 0;

    for (let t = 0; t < 1; t += step, index++) {
      for (const side of [-1, 1]) {
        const { pos, yaw } = this.poseAt(t, side * (ROAD_HALF_WIDTH + 0.35));
        const geo = new THREE.BoxGeometry(0.35, 0.09, 2.3);
        geo.rotateY(yaw);
        geo.translate(pos.x, 0.045, pos.z);
        (index % 2 === 0 ? a : b).push(geo);
      }
    }

    this.environmentGroup.add(new THREE.Mesh(mergeGeometries(a), new THREE.MeshStandardMaterial({ color: colorA, roughness: 0.56 })));
    this.environmentGroup.add(new THREE.Mesh(mergeGeometries(b), new THREE.MeshStandardMaterial({ color: colorB, roughness: 0.56 })));
  }

  /**
   * Cyberpunk garage wrapped around the parked car at the track start —
   * the menu camera sweeps inside it, and on GO you drive out the open end.
   */
  private createGarage(): void {
    const { pos, yaw } = this.poseAt(0.002, 0);
    const g = new THREE.Group();
    g.position.set(pos.x, 0, pos.z);
    g.rotation.y = yaw;

    const wallMat = concreteMaterial(0x777b88, [6, 2]);
    const trimMat = darkMetalMaterial(0x0b0e14, 0.5);
    const glowPink = emissiveMaterial(0xff168c, 4.2);
    const glowTeal = emissiveMaterial(0x20eaf0, 4.2);

    // dark floor pad over the road inside
    const pad = new THREE.Mesh(
      new THREE.PlaneGeometry(19, 25),
      wetAsphaltMaterial(0.92, [5, 5])
    );
    pad.rotation.x = -Math.PI / 2;
    pad.position.set(0, 0.02, -2.5);
    pad.receiveShadow = true;
    g.add(pad);

    // shell: two side walls, ceiling, back wall; front (toward the road) open
    for (const side of [-1, 1]) {
      const wall = new THREE.Mesh(new THREE.BoxGeometry(0.5, 5.4, 25), wallMat);
      wall.position.set(side * 9, 2.7, -2.5);
      g.add(wall);
    }
    const ceiling = new THREE.Mesh(new THREE.BoxGeometry(18.5, 0.4, 25), wallMat);
    ceiling.position.set(0, 5.6, -2.5);
    g.add(ceiling);
    const back = new THREE.Mesh(new THREE.BoxGeometry(18.5, 5.4, 0.5), wallMat);
    back.position.set(0, 2.7, 9.8);
    g.add(back);

    // roller door on the back wall, slats + neon frame
    const door = new THREE.Mesh(new THREE.BoxGeometry(5.2, 4.0, 0.2), trimMat);
    door.position.set(0, 2.0, 9.5);
    g.add(door);
    for (let i = 0; i < 5; i++) {
      const slat = new THREE.Mesh(new THREE.BoxGeometry(5.0, 0.06, 0.22), darkMetalMaterial(0x252a35, 0.42));
      slat.position.set(0, 0.6 + i * 0.75, 9.48);
      g.add(slat);
    }
    for (const side of [-1, 1]) {
      const jamb = new THREE.Mesh(new THREE.BoxGeometry(0.12, 4.1, 0.12), glowTeal);
      jamb.position.set(side * 2.7, 2.0, 9.42);
      g.add(jamb);
    }
    const lintel = new THREE.Mesh(new THREE.BoxGeometry(5.5, 0.12, 0.12), glowTeal);
    lintel.position.set(0, 4.1, 9.42);
    g.add(lintel);

    // neon strips down both interior walls
    for (const side of [-1, 1]) {
      const low = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.14, 23), glowPink);
      low.position.set(side * 8.7, 1.5, -2.5);
      g.add(low);
      const high = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.14, 23), glowTeal);
      high.position.set(side * 8.7, 3.4, -2.5);
      g.add(high);
    }

    // ceiling light panel over the car + light pool beneath
    const panel = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.12, 4.2), emissiveMaterial(0xffdbea, 1.35));
    panel.position.set(0, 5.34, 0);
    g.add(panel);
    // actual lights so the car and walls read inside the shell
    const lamp = new THREE.PointLight(0xffe8f4, 310, 22, 1.7);
    lamp.position.set(0, 4.6, 0);
    g.add(lamp);
    const fillPink = new THREE.PointLight(0xff168c, 125, 15, 1.8);
    fillPink.position.set(-6, 1.8, 2);
    g.add(fillPink);
    const fillTeal = new THREE.PointLight(0x20eaf0, 125, 15, 1.8);
    fillTeal.position.set(6, 1.8, -6);
    g.add(fillTeal);

    const carKey = new THREE.SpotLight(0xffd7e8, 720, 24, Math.PI / 4.5, 0.48, 1.4);
    carKey.position.set(-4.5, 6.2, 6.5);
    carKey.target.position.set(0, 0.7, 0);
    carKey.castShadow = true;
    carKey.shadow.mapSize.set(1024, 1024);
    g.add(carKey, carKey.target);

    const cyanRim = new THREE.SpotLight(0x20eaf0, 360, 20, Math.PI / 4, 0.6, 1.4);
    cyanRim.position.set(6.5, 2.8, -3.5);
    cyanRim.target.position.set(0, 0.65, 0.4);
    g.add(cyanRim, cyanRim.target);

    const pinkRim = new THREE.SpotLight(0xff168c, 330, 18, Math.PI / 4, 0.6, 1.5);
    pinkRim.position.set(-6, 2.2, 2.5);
    pinkRim.target.position.set(0, 0.5, 0);
    g.add(pinkRim, pinkRim.target);

    // Distant skyline gives the menu a pursuit-city horizon while remaining
    // outside the playable road corridor of the first operation.
    const skyline = new THREE.Group();
    skyline.position.z = -58;
    const towerSpecs = [
      [-34, 10, 30, 10], [-24, 7, 21, 8], [-16, 8, 36, 9], [-10, 5, 18, 7],
      [11, 6, 23, 8], [18, 9, 39, 10], [28, 8, 27, 9], [37, 11, 34, 12],
    ];
    towerSpecs.forEach(([x, w, h, d], index) => {
      const facade = facadeTexture(200 + index * 19);
      facade.repeat.set(Math.max(1, Math.round(w / 5)), Math.max(2, Math.round(h / 6)));
      const tower = new THREE.Mesh(
        new THREE.BoxGeometry(w, h, d),
        new THREE.MeshStandardMaterial({
          color: 0x22283a,
          map: facade,
          emissiveMap: facade,
          emissive: index % 2 ? 0x19365d : 0x421738,
          emissiveIntensity: 0.85,
          roughness: 0.78,
          metalness: 0.08,
        }),
      );
      tower.position.set(x, h * 0.5, 0);
      skyline.add(tower);
      if (index % 3 === 0) {
        const crown = new THREE.Mesh(
          new THREE.BoxGeometry(w * 0.62, 0.22, d + 0.15),
          index % 2 ? glowTeal : glowPink,
        );
        crown.position.set(x, h + 0.1, 0);
        skyline.add(crown);
      }
    });
    g.add(skyline);
    const pool = new THREE.Mesh(
      new THREE.CircleGeometry(4.4, 14),
      new THREE.MeshBasicMaterial({
        color: 0x5a1b4a,
        blending: THREE.AdditiveBlending,
        transparent: true,
        opacity: 0.3,
        depthWrite: false,
      })
    );
    pool.rotation.x = -Math.PI / 2;
    pool.position.set(0, 0.035, 0);
    g.add(pool);

    // garage clutter: crates, barrels, tire stack, tool cabinet
    const crateMat = darkMetalMaterial(0x222632, 0.64);
    const crateA = new THREE.Mesh(new THREE.BoxGeometry(1.3, 1.3, 1.3), crateMat);
    crateA.position.set(7.2, 0.65, 1.4);
    g.add(crateA);
    const crateB = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.9, 0.9), crateMat);
    crateB.position.set(7.4, 1.75, 1.3);
    crateB.rotation.y = 0.4;
    g.add(crateB);
    const crateEdge = new THREE.Mesh(new THREE.BoxGeometry(1.34, 0.08, 1.34), glowTeal);
    crateEdge.position.set(7.2, 1.32, 1.4);
    g.add(crateEdge);

    const barrelMat = paintedMetalMaterial(0x8f2816, 0.4);
    for (const z of [0.6, 1.9]) {
      const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 1.1, 10), barrelMat);
      barrel.position.set(-7.4, 0.55, z);
      g.add(barrel);
    }

    const tireMat = new THREE.MeshStandardMaterial({ color: 0x08090c, roughness: 0.96 });
    for (let i = 0; i < 3; i++) {
      const tire = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 0.28, 12), tireMat);
      tire.position.set(-7.1, 0.16 + i * 0.3, 3.3);
      g.add(tire);
    }

    const cabinet = new THREE.Mesh(new THREE.BoxGeometry(1.7, 2.3, 0.55), trimMat);
    cabinet.position.set(7.9, 1.15, -3.6);
    g.add(cabinet);
    const handle = new THREE.Mesh(new THREE.BoxGeometry(0.08, 1.6, 0.08), glowPink);
    handle.position.set(7.6, 1.15, -3.28);
    g.add(handle);

    // A real practical sign gives the cyan light an identifiable source.
    const sign = new THREE.Mesh(
      new THREE.PlaneGeometry(4.8, 1.3),
      new THREE.MeshBasicMaterial({
        map: neonSignTexture(),
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      })
    );
    sign.position.set(-8.68, 3.35, -2.5);
    sign.rotation.y = Math.PI / 2;
    g.add(sign);

    // Broken reflective patches catch the practical lights without turning
    // the whole floor into a perfect mirror.
    const puddleMat = new THREE.MeshPhysicalMaterial({
      color: 0x08101b,
      roughness: 0.12,
      metalness: 0.08,
      clearcoat: 1,
      clearcoatRoughness: 0.06,
      transparent: true,
      opacity: 0.56,
      depthWrite: false,
    });
    for (const [x, z, sx, sz] of [
      [-4.9, -5.8, 2.5, 0.7],
      [4.2, -1.8, 1.8, 0.5],
      [-1.8, 2.4, 1.35, 0.42],
    ] as Array<[number, number, number, number]>) {
      const puddle = new THREE.Mesh(new THREE.CircleGeometry(1, 18), puddleMat.clone());
      puddle.rotation.x = -Math.PI / 2;
      puddle.scale.set(sx, sz, 1);
      puddle.position.set(x, 0.043, z);
      puddle.renderOrder = 1;
      g.add(puddle);
    }

    // Sparse streaks establish that the surface is wet. The group loops over
    // itself so it remains cheap and deterministic in both menu and gameplay.
    const rainPositions: number[] = [];
    for (let i = 0; i < 150; i++) {
      const x = (this.random.next() - 0.5) * 17;
      const y = this.random.next() * 10;
      const z = (this.random.next() - 0.5) * 22 - 2;
      rainPositions.push(x, y, z, x - 0.04, y - 0.42, z + 0.08);
    }
    const rainGeometry = new THREE.BufferGeometry();
    rainGeometry.setAttribute('position', new THREE.Float32BufferAttribute(rainPositions, 3));
    this.garageRain = new THREE.LineSegments(
      rainGeometry,
      new THREE.LineBasicMaterial({
        color: 0xb9f9ff,
        transparent: true,
        opacity: 0.13,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    this.garageRain.renderOrder = 2;
    g.add(this.garageRain);

    this.environmentGroup.add(g);
  }

  private createArch(t: number, kind: 'start' | 'finish'): void {
    const { pos, yaw } = this.poseAt(t, 0);
    const group = new THREE.Group();
    group.position.set(pos.x, 0, pos.z);
    group.rotation.y = yaw;

    const pillarMat = new THREE.MeshLambertMaterial({ color: 0xdddddd });
    for (const side of [-1, 1]) {
      const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.5, 6.5, 0.5), pillarMat);
      pillar.position.set(side * (ROAD_HALF_WIDTH + 1), 3.25, 0);
      pillar.castShadow = true;
      group.add(pillar);
    }

    const banner = new THREE.Mesh(
      new THREE.BoxGeometry((ROAD_HALF_WIDTH + 1) * 2 + 0.5, 1.2, 0.4),
      new THREE.MeshLambertMaterial({ color: kind === 'finish' ? 0xcc2222 : 0x2255cc })
    );
    banner.position.y = 6.5;
    group.add(banner);

    if (kind === 'finish') {
      const checks = checkerTexture();
      checks.repeat.set(10, 1);
      const strip = new THREE.Mesh(
        new THREE.PlaneGeometry(10, 0.5),
        new THREE.MeshBasicMaterial({ map: checks, side: THREE.DoubleSide })
      );
      strip.position.set(0, 5.6, 0.21);
      group.add(strip);
    }

    this.environmentGroup.add(group);
  }

  /** Ground plane spanning the whole track. */
  private createGroundPlane(map: THREE.Texture, tileMeters: number): void {
    const mid = this.railPath.getPosition(0.5);
    map.repeat.set(800 / tileMeters, 1800 / tileMeters);
    const geo = new THREE.PlaneGeometry(800, 1800, 1, 1);
    const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ map, roughness: 0.9, metalness: 0 }));
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(mid.x, -0.06, mid.z);
    mesh.receiveShadow = true;
    this.environmentGroup.add(mesh);
  }

  private createShoulders(map: THREE.Texture, width = 3.5): void {
    this.environmentGroup.add(
      this.buildRibbon(-ROAD_HALF_WIDTH - width, -ROAD_HALF_WIDTH, -0.03, { map, tile: 4, receiveShadow: true })
    );
    this.environmentGroup.add(
      this.buildRibbon(ROAD_HALF_WIDTH, ROAD_HALF_WIDTH + width, -0.03, { map: map.clone(), tile: 4, receiveShadow: true })
    );
  }

  // ---------------------------------------------------------------
  // Countryside
  // ---------------------------------------------------------------

  private buildCountryside(): void {
    this.createGroundPlane(grassTexture(), 4);
    this.createShoulders(dirtTexture());
    this.createReflectorPosts();
    this.createConiferForest();
    this.createHills(0x3d6b35);
    this.createBrickBuildings();
  }

  private createReflectorPosts(): void {
    const postGeos: THREE.BufferGeometry[] = [];
    const reflectorGeos: THREE.BufferGeometry[] = [];
    const step = 42 / this.railPath.length;

    for (let t = step; t < 1; t += step) {
      for (const side of [-1, 1]) {
        const { pos, yaw } = this.poseAt(t, side * (ROAD_HALF_WIDTH + 2.3));
        const post = new THREE.CylinderGeometry(0.05, 0.05, 1.2, 6);
        post.translate(pos.x, 0.6, pos.z);
        postGeos.push(post);

        const reflector = new THREE.BoxGeometry(0.3, 0.2, 0.05);
        reflector.rotateY(yaw);
        reflector.translate(pos.x, 1.25, pos.z);
        reflectorGeos.push(reflector);
      }
    }

    this.environmentGroup.add(new THREE.Mesh(mergeGeometries(postGeos), new THREE.MeshLambertMaterial({ color: 0xcccccc })));
    this.environmentGroup.add(new THREE.Mesh(mergeGeometries(reflectorGeos), new THREE.MeshBasicMaterial({ color: 0xff6600 })));
  }

  private createConiferForest(): void {
    const trunkGeos: THREE.BufferGeometry[] = [];
    const canopyBuckets: THREE.BufferGeometry[][] = [[], [], [], []];
    const leafColors = [0x2d6b2d, 0x3a8c3a, 0x1f5c1f, 0x4a9c3a];

    let t = 0.01;
    while (t < 0.99) {
      for (const side of [-1, 1]) {
        if (this.random.next() < 0.25) continue;
        const { pos } = this.poseAt(t, side * (11 + this.random.next() * 38));

        const trunkH = 1.5 + this.random.next() * 1.5;
        const trunk = new THREE.CylinderGeometry(0.15, 0.25, trunkH, 6);
        trunk.translate(pos.x, trunkH / 2, pos.z);
        trunkGeos.push(trunk);

        const bucket = Math.floor(this.random.next() * leafColors.length);
        const layers = 2 + Math.floor(this.random.next() * 2);
        for (let l = 0; l < layers; l++) {
          const radius = 1.8 - l * 0.4 + this.random.next() * 0.3;
          const cone = new THREE.ConeGeometry(radius, 2.0 - l * 0.3, 7);
          cone.translate(pos.x, trunkH + l * 1.2 + 0.5, pos.z);
          canopyBuckets[bucket].push(cone);
        }
      }
      t += (14 + this.random.next() * 18) / this.railPath.length;
    }

    const trunks = new THREE.Mesh(mergeGeometries(trunkGeos), new THREE.MeshLambertMaterial({ color: 0x5c3a1e }));
    trunks.castShadow = true;
    this.environmentGroup.add(trunks);

    canopyBuckets.forEach((bucket, i) => {
      if (bucket.length === 0) return;
      const canopies = new THREE.Mesh(mergeGeometries(bucket), new THREE.MeshLambertMaterial({ color: leafColors[i] }));
      canopies.castShadow = true;
      this.environmentGroup.add(canopies);
    });
  }

  private createHills(color: number): void {
    const hillMat = new THREE.MeshLambertMaterial({ color });
    const spots = [
      { t: 0.12, side: -1, sx: 90, sy: 22, sz: 110 },
      { t: 0.2, side: 1, sx: 100, sy: 28, sz: 120 },
      { t: 0.38, side: -1, sx: 80, sy: 18, sz: 95 },
      { t: 0.5, side: 1, sx: 120, sy: 32, sz: 140 },
      { t: 0.63, side: -1, sx: 100, sy: 24, sz: 120 },
      { t: 0.78, side: 1, sx: 85, sy: 20, sz: 100 },
      { t: 0.92, side: -1, sx: 110, sy: 30, sz: 130 },
    ];
    for (const h of spots) {
      // footprint radius is max(sx, sz): keep the whole base clear of the road
      const clearance = Math.max(h.sx, h.sz) + ROAD_HALF_WIDTH + 30;
      const { pos } = this.poseAt(h.t, h.side * clearance);
      const geo = new THREE.SphereGeometry(1, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2);
      geo.scale(h.sx, h.sy, h.sz);
      const hill = new THREE.Mesh(geo, hillMat);
      hill.position.set(pos.x, 0, pos.z);
      this.environmentGroup.add(hill);
    }
  }

  private createBrickBuildings(): void {
    const configs = [
      { t: 0.06, side: 1, w: 8, h: 5, d: 6, color: 0xc4a882 },
      { t: 0.14, side: -1, w: 12, h: 4, d: 8, color: 0x887766 },
      { t: 0.27, side: 1, w: 6, h: 6, d: 6, color: 0xaa8866 },
      { t: 0.35, side: -1, w: 15, h: 5, d: 10, color: 0x998877 },
      { t: 0.47, side: 1, w: 10, h: 4, d: 7, color: 0xbbaa88 },
      { t: 0.58, side: -1, w: 8, h: 7, d: 8, color: 0x8888aa },
      { t: 0.71, side: 1, w: 12, h: 5, d: 9, color: 0xa89878 },
      { t: 0.84, side: -1, w: 9, h: 6, d: 7, color: 0x9a8a7a },
    ];

    const brickCache = new Map<number, THREE.Texture>();
    for (const b of configs) {
      let base = brickCache.get(b.color);
      if (!base) {
        base = brickTexture(`#${b.color.toString(16).padStart(6, '0')}`);
        brickCache.set(b.color, base);
      }
      const tex = base.clone();
      tex.needsUpdate = true;
      tex.repeat.set(Math.max(1, Math.round(b.w / 3)), Math.max(1, Math.round(b.h / 3)));

      const { pos, yaw } = this.poseAt(b.t, b.side * (15 + b.w / 2));
      const group = new THREE.Group();
      group.position.set(pos.x, 0, pos.z);
      group.rotation.y = yaw;

      const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), new THREE.MeshLambertMaterial({ map: tex }));
      body.position.y = b.h / 2;
      body.castShadow = true;
      body.receiveShadow = true;
      group.add(body);

      const roof = new THREE.Mesh(
        new THREE.BoxGeometry(b.w + 0.5, 0.3, b.d + 0.5),
        new THREE.MeshLambertMaterial({ color: 0x664433 })
      );
      roof.position.y = b.h + 0.15;
      group.add(roof);

      this.environmentGroup.add(group);
    }
  }

  // ---------------------------------------------------------------
  // City (night)
  // ---------------------------------------------------------------

  private buildCity(): void {
    this.createGroundPlane(darkAsphaltTexture(), 6);

    // Sidewalks
    const walk = concreteTexture();
    this.environmentGroup.add(this.buildRibbon(-(ROAD_HALF_WIDTH + 4), -ROAD_HALF_WIDTH, 0.05, { map: walk, tile: 4, receiveShadow: true }));
    this.environmentGroup.add(this.buildRibbon(ROAD_HALF_WIDTH, ROAD_HALF_WIDTH + 4, 0.05, { map: walk.clone(), tile: 4, receiveShadow: true }));

    // Towers with lit windows
    const facades = [facadeTexture(11), facadeTexture(23), facadeTexture(47), facadeTexture(89)];
    let t = 0.015;
    let i = 0;
    while (t < 0.985) {
      for (const side of [-1, 1]) {
        if (this.random.next() < 0.15) continue;
        const w = 10 + this.random.next() * 10;
        const h = 12 + this.random.next() * 22;
        const d = 10 + this.random.next() * 8;
        const { pos, yaw } = this.poseAt(t, side * (11 + w / 2));

        const tex = facades[i++ % facades.length].clone();
        tex.needsUpdate = true;
        tex.repeat.set(Math.max(1, Math.round(w / 6)), Math.max(1, Math.round(h / 6)));

        const tower = new THREE.Mesh(
          new THREE.BoxGeometry(w, h, d),
          new THREE.MeshBasicMaterial({ map: tex }) // self-lit at night
        );
        tower.position.set(pos.x, h / 2, pos.z);
        tower.rotation.y = yaw;
        this.environmentGroup.add(tower);

        // occasional rooftop neon slab
        if (this.random.next() < 0.35) {
          const neonColors = [0xff2288, 0x22ddff, 0xffcc22, 0x66ff44];
          const neon = new THREE.Mesh(
            new THREE.BoxGeometry(w * 0.5, 1.2, 0.4),
            new THREE.MeshBasicMaterial({ color: neonColors[Math.floor(this.random.next() * neonColors.length)] })
          );
          neon.position.set(pos.x, h + 1, pos.z);
          neon.rotation.y = yaw;
          this.environmentGroup.add(neon);
        }
      }
      t += (30 + this.random.next() * 14) / this.railPath.length;
    }

    // Street lights — closer together, with glowing pools on the asphalt so
    // enemies and obstacles silhouette against lit patches of road
    const poles: THREE.BufferGeometry[] = [];
    const lamps: THREE.BufferGeometry[] = [];
    const pools: THREE.BufferGeometry[] = [];
    const step = 26 / this.railPath.length;
    for (let s = step; s < 1; s += step) {
      for (const side of [-1, 1]) {
        const { pos, yaw } = this.poseAt(s, side * (ROAD_HALF_WIDTH + 2));
        const pole = new THREE.CylinderGeometry(0.08, 0.1, 5.2, 6);
        pole.translate(pos.x, 2.6, pos.z);
        poles.push(pole);

        const arm = new THREE.BoxGeometry(0.12, 0.12, 1.6);
        arm.rotateY(yaw + Math.PI / 2);
        arm.translate(pos.x - Math.sin(yaw + Math.PI / 2) * -0.8 * side, 5.1, pos.z - Math.cos(yaw + Math.PI / 2) * -0.8 * side);
        poles.push(arm);

        const lamp = new THREE.BoxGeometry(0.5, 0.15, 0.5);
        lamp.translate(pos.x, 5.05, pos.z);
        lamps.push(lamp);

        // light pool spilling onto the road
        const { pos: poolPos } = this.poseAt(s, side * (ROAD_HALF_WIDTH - 2.2));
        const pool = new THREE.CircleGeometry(4.2, 12);
        pool.rotateX(-Math.PI / 2);
        pool.translate(poolPos.x, 0.008, poolPos.z);
        pools.push(pool);
      }
    }
    this.environmentGroup.add(new THREE.Mesh(mergeGeometries(poles), new THREE.MeshLambertMaterial({ color: 0x3a3a40 })));
    this.environmentGroup.add(new THREE.Mesh(mergeGeometries(lamps), new THREE.MeshBasicMaterial({ color: 0xffe9a8 })));
    const poolMat = new THREE.MeshBasicMaterial({
      color: 0x40381e,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    });
    this.environmentGroup.add(new THREE.Mesh(mergeGeometries(pools), poolMat));
  }

  // ---------------------------------------------------------------
  // Tunnel
  // ---------------------------------------------------------------

  private buildTunnel(): void {
    const wallTex = concreteTexture();
    const wallDark = new THREE.MeshBasicMaterial({ map: wallTex, color: 0x8a8a8a });

    // Side walls
    this.environmentGroup.add(this.buildWall(-(ROAD_HALF_WIDTH + 2.5), 0, 5.5, wallDark, 6));
    this.environmentGroup.add(this.buildWall(ROAD_HALF_WIDTH + 2.5, 0, 5.5, wallDark, 6));

    // Ceiling + floor shoulders
    const ceilTex = concreteTexture();
    ceilTex.repeat.set(2, 1);
    this.environmentGroup.add(this.buildRibbon(-(ROAD_HALF_WIDTH + 2.5), ROAD_HALF_WIDTH + 2.5, 5.5, { map: ceilTex, color: 0x777777, basic: true, tile: 6 }));
    const shoulderTex = darkAsphaltTexture();
    this.environmentGroup.add(this.buildRibbon(-(ROAD_HALF_WIDTH + 2.5), -ROAD_HALF_WIDTH, -0.01, { map: shoulderTex, basic: true, color: 0x999999, tile: 4 }));
    this.environmentGroup.add(this.buildRibbon(ROAD_HALF_WIDTH, ROAD_HALF_WIDTH + 2.5, -0.01, { map: shoulderTex.clone(), basic: true, color: 0x999999, tile: 4 }));

    // Ceiling light strips every ~14m — the tunnel's rhythm
    const lightGeos: THREE.BufferGeometry[] = [];
    const step = 14 / this.railPath.length;
    for (let t = 0; t < 1; t += step) {
      const { pos, yaw } = this.poseAt(t, 0);
      const geo = new THREE.PlaneGeometry(1.2, 3.0);
      geo.rotateX(Math.PI / 2);
      geo.rotateY(yaw);
      geo.translate(pos.x, 5.45, pos.z);
      lightGeos.push(geo);
    }
    this.environmentGroup.add(new THREE.Mesh(
      mergeGeometries(lightGeos),
      new THREE.MeshBasicMaterial({ color: 0xfff2cc, side: THREE.DoubleSide })
    ));

    // Emergency wall lights, alternating sides
    const wallLights: THREE.BufferGeometry[] = [];
    const wStep = 28 / this.railPath.length;
    let side = 1;
    for (let t = wStep; t < 1; t += wStep, side *= -1) {
      const { pos, yaw } = this.poseAt(t, side * (ROAD_HALF_WIDTH + 2.3));
      const geo = new THREE.BoxGeometry(0.5, 0.25, 0.12);
      geo.rotateY(yaw);
      geo.translate(pos.x, 1.4, pos.z);
      wallLights.push(geo);
    }
    this.environmentGroup.add(new THREE.Mesh(
      mergeGeometries(wallLights),
      new THREE.MeshBasicMaterial({ color: 0xff2d95 })
    ));
  }

  /** Vertical wall following the curve at a fixed lateral offset. */
  private buildWall(lateral: number, y0: number, y1: number, material: THREE.Material, tile = 6): THREE.Mesh {
    const step = 3 / this.railPath.length;
    const positions: number[] = [];
    const indices: number[] = [];
    const uvs: number[] = [];

    let row = 0;
    for (let t = 0; t <= 1 + 1e-9; t += step, row++) {
      const tc = Math.min(1, t);
      const a = this.railPath.getPosition(tc, lateral);
      positions.push(a.x, y0, a.z, a.x, y1, a.z);
      const v = (tc * this.railPath.length) / tile;
      uvs.push(v, 0, v, 1);

      if (row > 0) {
        const base = (row - 1) * 2;
        indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setIndex(indices);
    geo.computeVertexNormals();

    const mat = material.clone();
    (mat as THREE.MeshBasicMaterial).side = THREE.DoubleSide;
    return new THREE.Mesh(geo, mat);
  }

  // ---------------------------------------------------------------
  // Desert
  // ---------------------------------------------------------------

  private buildDesert(): void {
    this.createGroundPlane(sandTexture(), 5);
    this.createShoulders(sandTexture());

    // Mesas — flat-topped rock stacks
    const rockTex = rockTexture();
    const mesaSpots = [
      { t: 0.08, side: -1, w: 60, h: 26 },
      { t: 0.22, side: 1, w: 80, h: 34 },
      { t: 0.36, side: -1, w: 70, h: 30 },
      { t: 0.5, side: 1, w: 55, h: 22 },
      { t: 0.62, side: -1, w: 75, h: 38 },
      { t: 0.78, side: 1, w: 90, h: 28 },
      { t: 0.9, side: -1, w: 65, h: 32 },
    ];
    for (const m of mesaSpots) {
      // base radius is w * 0.5 — keep it well clear of the road
      const { pos } = this.poseAt(m.t, m.side * (m.w * 0.5 + ROAD_HALF_WIDTH + 35));
      const tex = rockTex.clone();
      tex.needsUpdate = true;
      tex.repeat.set(Math.round(m.w / 8), Math.round(m.h / 8));
      const mesa = new THREE.Mesh(
        new THREE.CylinderGeometry(m.w * 0.32, m.w * 0.5, m.h, 8),
        new THREE.MeshLambertMaterial({ map: tex })
      );
      mesa.position.set(pos.x, m.h / 2, pos.z);
      mesa.rotation.y = this.random.next() * Math.PI;
      this.environmentGroup.add(mesa);
    }

    // Cacti
    const cactusGeos: THREE.BufferGeometry[] = [];
    let t = 0.02;
    while (t < 0.98) {
      for (const side of [-1, 1]) {
        if (this.random.next() < 0.45) continue;
        const { pos } = this.poseAt(t, side * (10 + this.random.next() * 30));
        const h = 2 + this.random.next() * 2;
        const trunk = new THREE.CylinderGeometry(0.28, 0.32, h, 6);
        trunk.translate(pos.x, h / 2, pos.z);
        cactusGeos.push(trunk);
        // one or two arms
        for (let arm = 0; arm < 1 + Math.floor(this.random.next() * 2); arm++) {
          const dir = this.random.next() < 0.5 ? -1 : 1;
          const armH = 0.8 + this.random.next() * 0.8;
          const seg = new THREE.CylinderGeometry(0.18, 0.18, armH, 6);
          seg.translate(pos.x + dir * 0.55, h * 0.55 + armH / 2, pos.z);
          cactusGeos.push(seg);
          const joint = new THREE.CylinderGeometry(0.18, 0.18, 0.6, 6);
          joint.rotateZ(Math.PI / 2);
          joint.translate(pos.x + dir * 0.32, h * 0.55, pos.z);
          cactusGeos.push(joint);
        }
      }
      t += (20 + this.random.next() * 24) / this.railPath.length;
    }
    const cacti = new THREE.Mesh(mergeGeometries(cactusGeos), new THREE.MeshLambertMaterial({ color: 0x3f7a3a }));
    cacti.castShadow = true;
    this.environmentGroup.add(cacti);

    // Scattered rocks
    const rockGeos: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 60; i++) {
      const { pos } = this.poseAt(this.random.next(), (this.random.next() < 0.5 ? -1 : 1) * (9 + this.random.next() * 45));
      const size = 0.4 + this.random.next() * 1.2;
      const rock = new THREE.IcosahedronGeometry(size, 0);
      rock.translate(pos.x, size * 0.5, pos.z);
      rockGeos.push(rock);
    }
    this.environmentGroup.add(new THREE.Mesh(mergeGeometries(rockGeos), new THREE.MeshLambertMaterial({ color: 0xb08a5c })));
  }

  // ---------------------------------------------------------------
  // Swamp
  // ---------------------------------------------------------------

  private buildSwamp(): void {
    this.createGroundPlane(murkTexture(), 4);
    this.createShoulders(murkTexture());

    // Water pools
    const waterTex = waterTexture();
    for (let i = 0; i < 16; i++) {
      const { pos } = this.poseAt(this.random.next(), (this.random.next() < 0.5 ? -1 : 1) * (12 + this.random.next() * 40));
      const r = 8 + this.random.next() * 18;
      const tex = waterTex.clone();
      tex.needsUpdate = true;
      tex.repeat.set(r / 3, r / 3);
      const pool = new THREE.Mesh(
        new THREE.CircleGeometry(r, 10),
        new THREE.MeshBasicMaterial({ map: tex })
      );
      pool.rotation.x = -Math.PI / 2;
      pool.position.set(pos.x, -0.04, pos.z);
      this.environmentGroup.add(pool);
    }

    // Dead trees + mossy canopies
    const deadGeos: THREE.BufferGeometry[] = [];
    const mossTrunks: THREE.BufferGeometry[] = [];
    const mossCanopies: THREE.BufferGeometry[] = [];
    let t = 0.015;
    while (t < 0.985) {
      for (const side of [-1, 1]) {
        if (this.random.next() < 0.3) continue;
        const { pos } = this.poseAt(t, side * (9 + this.random.next() * 32));

        if (this.random.next() < 0.45) {
          // bare dead tree
          const h = 3 + this.random.next() * 3;
          const trunk = new THREE.CylinderGeometry(0.12, 0.28, h, 5);
          trunk.translate(pos.x, h / 2, pos.z);
          deadGeos.push(trunk);
          for (let branch = 0; branch < 2 + Math.floor(this.random.next() * 2); branch++) {
            const bh = 1 + this.random.next() * 1.4;
            const seg = new THREE.CylinderGeometry(0.05, 0.09, bh, 4);
            seg.rotateZ((this.random.next() - 0.5) * 1.8);
            seg.rotateY(this.random.next() * Math.PI * 2);
            seg.translate(pos.x, h * (0.55 + this.random.next() * 0.35), pos.z);
            deadGeos.push(seg);
          }
        } else {
          // mossy broadleaf: trunk + squashed sphere canopy
          const h = 2.2 + this.random.next() * 1.6;
          const trunk = new THREE.CylinderGeometry(0.2, 0.3, h, 6);
          trunk.translate(pos.x, h / 2, pos.z);
          mossTrunks.push(trunk);
          const r = 1.6 + this.random.next() * 1.2;
          const canopy = new THREE.SphereGeometry(r, 7, 5);
          canopy.scale(1, 0.55, 1);
          canopy.translate(pos.x, h + r * 0.3, pos.z);
          mossCanopies.push(canopy);
        }
      }
      t += (13 + this.random.next() * 15) / this.railPath.length;
    }
    this.environmentGroup.add(new THREE.Mesh(mergeGeometries(deadGeos), new THREE.MeshLambertMaterial({ color: 0x4a4036 })));
    this.environmentGroup.add(new THREE.Mesh(mergeGeometries(mossTrunks), new THREE.MeshLambertMaterial({ color: 0x53483a })));
    const canopies = new THREE.Mesh(mergeGeometries(mossCanopies), new THREE.MeshLambertMaterial({ color: 0x44583a }));
    canopies.castShadow = true;
    this.environmentGroup.add(canopies);

    // Reed clusters near the road
    const reedGeos: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 90; i++) {
      const { pos } = this.poseAt(this.random.next(), (this.random.next() < 0.5 ? -1 : 1) * (8.2 + this.random.next() * 6));
      for (let r = 0; r < 4; r++) {
        const h = 0.7 + this.random.next() * 0.9;
        const reed = new THREE.CylinderGeometry(0.02, 0.03, h, 3);
        reed.translate(pos.x + (this.random.next() - 0.5) * 0.8, h / 2, pos.z + (this.random.next() - 0.5) * 0.8);
        reedGeos.push(reed);
      }
    }
    this.environmentGroup.add(new THREE.Mesh(mergeGeometries(reedGeos), new THREE.MeshLambertMaterial({ color: 0x6b7a45 })));
  }

  // ---------------------------------------------------------------

  /** Flat strip following the curve between two lateral offsets. */
  private buildRibbon(
    latA: number,
    latB: number,
    y: number,
    opts: {
      color?: number;
      map?: THREE.Texture;
      tile?: number; // meters per texture repeat along the ribbon
      receiveShadow?: boolean;
      basic?: boolean;
      material?: THREE.Material;
    }
  ): THREE.Mesh {
    const step = 3 / this.railPath.length;
    const tile = opts.tile ?? 6;
    const positions: number[] = [];
    const indices: number[] = [];
    const normals: number[] = [];
    const uvs: number[] = [];

    let row = 0;
    for (let t = 0; t <= 1 + 1e-9; t += step, row++) {
      const tc = Math.min(1, t);
      const a = this.railPath.getPosition(tc, latA);
      const b = this.railPath.getPosition(tc, latB);
      positions.push(a.x, y, a.z, b.x, y, b.z);
      normals.push(0, 1, 0, 0, 1, 0);
      const v = (tc * this.railPath.length) / tile;
      uvs.push(0, v, 1, v);

      if (row > 0) {
        const base = (row - 1) * 2;
        indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setIndex(indices);

    const params = { color: opts.color ?? 0xffffff, map: opts.map ?? null };
    const mat = opts.material ?? (opts.basic
      ? new THREE.MeshBasicMaterial(params)
      : new THREE.MeshStandardMaterial({ ...params, roughness: 0.86, metalness: 0 }));
    const mesh = new THREE.Mesh(geo, mat);
    mesh.receiveShadow = opts.receiveShadow ?? false;
    return mesh;
  }
}

/** Dispose all children of a group (geometries, materials, textures). */
function disposeChildren(group: THREE.Group): void {
  const doomed = [...group.children];
  for (const child of doomed) {
    child.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const mat of mats) {
          for (const value of Object.values(mat)) {
            if (value instanceof THREE.Texture) value.dispose();
          }
          mat.dispose();
        }
      } else if (obj instanceof THREE.Line || obj instanceof THREE.Points) {
        obj.geometry.dispose();
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach((material) => material.dispose());
      }
    });
    group.remove(child);
  }
}

function neonSignTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 768;
  canvas.height = 208;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = "700 62px 'Audiowide', 'Courier New', monospace";
  ctx.shadowBlur = 26;
  ctx.shadowColor = '#ff168c';
  ctx.fillStyle = '#ff79c4';
  ctx.fillText('NEON DRIVE', 384, 72);
  ctx.font = "700 44px 'Courier New', monospace";
  ctx.shadowBlur = 22;
  ctx.shadowColor = '#20eaf0';
  ctx.fillStyle = '#8effff';
  ctx.fillText('NO SIGNAL', 384, 146);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  return texture;
}
